import React, { useState } from "react";
import { CalendarDays } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Field } from "../ui/Field";
import type { BoardColumn } from "./boardColumns";

/**
 * Création d'un tableau : un nom pour l'humain, une date pour l'app (maquette 16d).
 *
 * La date n'est pas un confort d'affichage — c'est elle qui range le tableau dans son année et qui
 * date ensuite chaque réservation ajoutée. Un tableau sans date se retrouverait hors de tout écran,
 * d'où le bouton « Créer » désactivé tant que les deux champs ne sont pas remplis.
 */
export function NewBoardModal({
  onClose,
  onCreate,
  canReuseColumns,
  initialTitle = "",
  initialColumns,
}: {
  onClose: () => void;
  onCreate: (title: string, serviceDate: string, reuseColumns: boolean, columnsOverride?: BoardColumn[]) => void;
  /** Faux quand il n'existe aucun tableau antérieur : la case n'aurait alors rien à reprendre. */
  canReuseColumns: boolean;
  /** Duplication : le nom est repris, la date reste à choisir, les colonnes suivent la source. */
  initialTitle?: string;
  initialColumns?: BoardColumn[];
}) {
  const [title, setTitle] = useState(initialTitle);
  const [serviceDate, setServiceDate] = useState("");
  const [reuseColumns, setReuseColumns] = useState(canReuseColumns);

  const ready = !!title.trim() && !!serviceDate;

  const handleCreate = () => {
    if (!ready) return;
    onCreate(title.trim(), serviceDate, reuseColumns && canReuseColumns, initialColumns);
    onClose();
  };

  // « Mardi 14 juillet 2026 » sous le champ : relire la date en toutes lettres est le seul moyen de
  // repérer une saisie à côté (2025 au lieu de 2026, ou un jour de semaine impossible pour un service).
  const dateLabel = serviceDate
    ? new Date(`${serviceDate}T00:00:00`).toLocaleDateString("fr-FR", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      })
    : "";

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      icon={<CalendarDays size={17} />}
      title="Nouveau tableau"
      subtitle="Un nom pour vous, une date pour l'app. Toute réservation ajoutée au tableau portera cette date."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button onClick={handleCreate} disabled={!ready}>Créer le tableau</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Nom du service">
          {({ id }) => (
            <input
              id={id}
              className="field-input w-full"
              placeholder="Ex: Réveillon, Saint-Valentin…"
              value={title}
              autoFocus
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />
          )}
        </Field>

        <Field label="Date du service" help={dateLabel ? <span className="first-letter:uppercase">{dateLabel}</span> : undefined}>
          {({ id }) => (
            <input
              id={id}
              type="date"
              className="field-input w-full"
              value={serviceDate}
              onChange={(e) => setServiceDate(e.target.value)}
            />
          )}
        </Field>

        {canReuseColumns && (
          <label className="flex items-start gap-2.5 p-3 rounded-[10px] bg-surface-soft border border-border-soft cursor-pointer">
            <input
              type="checkbox"
              checked={reuseColumns}
              onChange={(e) => setReuseColumns(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-border accent-brand-600 shrink-0"
            />
            <span className="text-[12.5px] leading-snug text-content">
              <span className="font-bold block">Reprendre les colonnes du dernier tableau</span>
              <span className="text-[11px] text-content-muted">Sinon, les colonnes par défaut.</span>
            </span>
          </label>
        )}
      </div>
    </Modal>
  );
}
